/**
 * Paging for the speech bubble (src/components/Shell.tsx SpeechBubble). A line longer than the
 * bubble is shown a page at a time, and the page turns with the speech: the words have no
 * timestamps, so progress is taken as proportional to characters spoken, which for a ten-second
 * reading lands within a word or two of the voice. Pure functions; the component supplies the
 * measurement.
 */

/** Greedy word wrap: the longest run of words that `fits`, then the next. A single word too long for a line stands alone. */
export function wrapLines(text: string, fits: (candidate: string) => boolean): string[] {
  const words = text.trim().split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let line = "";
  for (const word of words) {
    const candidate = line ? `${line} ${word}` : word;
    if (!line || fits(candidate)) line = candidate;
    else {
      lines.push(line);
      line = word;
    }
  }
  if (line) lines.push(line);
  return lines;
}

/** Lines grouped into pages of `perPage`, each page one string. */
export function paginate(lines: string[], perPage: number): string[] {
  const pages: string[] = [];
  for (let i = 0; i < lines.length; i += perPage) pages.push(lines.slice(i, i + perPage).join(" "));
  return pages.length ? pages : [""];
}

/**
 * Which page is being spoken at `fraction` of the way through (0..1), weighting pages by their
 * characters, with a small lead so the words appear just before the voice reaches them.
 */
export function pageAt(pages: string[], fraction: number, lead = 0.06): number {
  if (pages.length < 2) return 0;
  const total = pages.reduce((n, p) => n + p.length, 0) || 1;
  const target = Math.min(1, Math.max(0, fraction + lead));
  let seen = 0;
  for (let i = 0; i < pages.length; i++) {
    seen += pages[i].length;
    if (target < seen / total) return i;
  }
  return pages.length - 1;
}
